import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useAuth } from '@/contexts/AuthContext';
import { useUpdateShopifyAdditionalSettings } from '@/hooks/useUpdateShopifyAdditionalSettings';

const formSchema = z.object({
  shopifyPublicationIds: z.string().optional(),
  shopifyExcludedVendors: z.string().optional(),
  shopifyOrderSource: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

export function AdditionalForm() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.Shopify.components.AdditionalForm',
  });

  const { user } = useAuth();

  const [isEditable, setIsEditable] = useState<boolean>(() => {
    return !(user?.customer.shopifyUrl && user?.customer.shopifyAccessToken);
  });

  const {
    formState: { isSubmitting, errors },
    register,
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      shopifyPublicationIds: user?.customer.shopifyPublicationIds ?? '',
      shopifyExcludedVendors: user?.customer.shopifyExcludedVendors ?? '',
      shopifyOrderSource: user?.customer.shopifyOrderSource ?? '',
    },
  });

  const { updateShopifyAdditionalSettings } =
    useUpdateShopifyAdditionalSettings();

  function handleEdit() {
    setIsEditable(true);
  }

  async function onSubmit(data: FormData) {
    try {
      const response = await updateShopifyAdditionalSettings({
        shopifyPublicationIds: data.shopifyPublicationIds || null,
        shopifyExcludedVendors: data.shopifyExcludedVendors || null,
        shopifyOrderSource: data.shopifyOrderSource || null,
      });

      if (response.success) {
        toast.success(response.message);
        setIsEditable(false);
      }
    } catch (error) {
      let message = t('toast.error');
      if (error instanceof AxiosError) {
        message = error.response?.data?.message ?? message;
      }
      toast.error(message);
    }
  }

  return (
    <form className="space-y-6" noValidate onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-1">
        <h3 className="text-lg font-bold">{t('title')}</h3>
        <p className="text-sm text-muted-foreground">{t('description')}</p>
      </div>

      <FormGroup
        className="space-y-2"
        error={{ message: errors.shopifyPublicationIds?.message, font: 'sm' }}
      >
        <Label htmlFor="shopifyPublicationIds">
          {t('shopifySalesChannelsLabel')}
        </Label>

        <Input
          id="shopifyPublicationIds"
          placeholder={t('shopifySalesChannelsPlaceholder')}
          className="w-full"
          disabled={!isEditable}
          {...register('shopifyPublicationIds')}
        />

        <p className="text-sm text-muted-foreground">
          {t('shopifySalesChannelsDescription')}
        </p>
      </FormGroup>

      <FormGroup
        className="space-y-2"
        error={{ message: errors.shopifyExcludedVendors?.message, font: 'sm' }}
      >
        <Label htmlFor="shopifyExcludedVendors">
          {t('shopifyExcludedVendorsLabel')}
        </Label>

        <Input
          id="shopifyExcludedVendors"
          placeholder={t('shopifyExcludedVendorsPlaceholder')}
          className="w-full"
          disabled={!isEditable}
          {...register('shopifyExcludedVendors')}
        />

        <p className="text-sm text-muted-foreground">
          {t('shopifyExcludedVendorsDescription')}
        </p>
      </FormGroup>

      <FormGroup
        className="space-y-2"
        error={{ message: errors.shopifyOrderSource?.message, font: 'sm' }}
      >
        <Label htmlFor="shopifyOrderSource">
          {t('shopifyOrderSourceLabel')}
        </Label>

        <Input
          id="shopifyOrderSource"
          placeholder={t('shopifyOrderSourcePlaceholder')}
          className="w-full"
          disabled={!isEditable}
          {...register('shopifyOrderSource')}
        />

        <p className="text-sm text-muted-foreground">
          {t('shopifyOrderSourceDescription')}
        </p>
      </FormGroup>

      {!isEditable && (
        <Button
          type="button"
          className="w-full bg-black text-white transition-colors hover:bg-gray-800"
          onClick={handleEdit}
        >
          {t('editButton')}
        </Button>
      )}

      {isEditable && (
        <Button
          type="submit"
          className="mt-auto w-full bg-black text-white transition-colors hover:bg-gray-800"
          disabled={isSubmitting}
        >
          {isSubmitting ? t('savingButton') : t('saveButton')}
        </Button>
      )}
    </form>
  );
}
